import React, { useMemo } from 'react';
import { Dialog, DialogTitle } from '@mui/material';
import { ScrapingTask, ScrapingTaskCreate } from '../hooks/useScrapingTasks';
import { extractKeywordsFromUrls, resolveSearchUrlTemplate } from '../utils/urlTemplates';
import TaskForm, { TaskFormSubmitData } from './TaskForm';

interface EditTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (taskData: Partial<ScrapingTaskCreate>) => void;
  task: ScrapingTask | null;
  definitions: any[];
  loading?: boolean;
}

const EditTaskDialog: React.FC<EditTaskDialogProps> = ({
  open,
  onClose,
  onSubmit,
  task,
  definitions,
  loading = false,
}) => {
  const definition = useMemo(
    () => definitions.find((d: any) => d.id === task?.marketplace_definition_id),
    [definitions, task?.marketplace_definition_id]
  );

  const keywords_text = useMemo(() => {
    if (!task) return '';
    const existing = (task as any)?.keywords_text;
    if (existing != null && String(existing).trim() !== '') return String(existing).trim();
    const inputMode = (task as any)?.input_mode || 'urls';
    const urls = task.urls && task.urls.length > 0 ? task.urls : [];
    if (inputMode !== 'keywords' || urls.length === 0) return '';
    const template = resolveSearchUrlTemplate(definition);
    return extractKeywordsFromUrls(urls, template);
  }, [task, definition]);

  const initialData = useMemo(() => {
    if (!task) return null;
    return {
      marketplace_definition_id: task.marketplace_definition_id,
      urls: task.urls && task.urls.length > 0 ? task.urls : [''],
      max_pages: task.max_pages || 1,
      max_stage2_products: task.max_stage2_products ?? 5,
      timeout_minutes: task.timeout_minutes || 10,
      input_mode: (task as any)?.input_mode || 'urls',
      brandName: (task as any)?.brand_name || '',
      productName: (task as any)?.product_name || '',
      keywords_text,
      execution_mode:
        task.execution_mode === 'extension' ? ('extension' as const) : ('internal' as const),
      extension_promoted_reason: task.extension_promoted_reason,
      extension_any_marketplace: Boolean(task.extension_any_marketplace),
    };
  }, [task, keywords_text]);

  if (!task || !initialData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Scraping Task</DialogTitle>
      <TaskForm
        key={task.id}
        mode="edit"
        definitions={definitions}
        loading={loading}
        initial={initialData}
        onCancel={onClose}
        onSubmit={(submitData: TaskFormSubmitData) => {
          // For edit mode, TaskForm already returns only changed fields
          onSubmit(submitData.taskData as Partial<ScrapingTaskCreate>);
        }}
      />
    </Dialog>
  );
};

export default EditTaskDialog;
